<template>
  <div class="bed-visualizer-collections bed-visualizer__page">
    <div class="bed-visualizer__head">
      <h1
        class="bed-visualizer__title h6"
        v-text="updateTitle()"
      />
      <button
        :aria-label="viewToggleLabel"
        class="bed-visualizer__view-toggle"
        :class="`bed-visualizer__view-toggle--${viewType}`"
        @click="toggleView"
      >
        <span class="bed-visualizer__view-toggle-inner" />
        <span class="bed-visualizer__view-toggle-inner" />
        <span class="bed-visualizer__view-toggle-inner" />
        <span class="bed-visualizer__view-toggle-inner" />
      </button>
    </div>

    <div
      ref="scrollElement"
      class="bed-visualizer__collections"
      :class="`bed-visualizer__collections--${viewType}`"
    >
      <template
        v-for="(collection, index) in collections"
        :key="index"
      >
        <collection-chip
          :block-class="'bed-visualizer'"
          :image="collection.image"
          :index="index"
          :is-active="isSelectedCollection(collection)"
          :loading="!collection.handle"
          :collection-title="collection?.title"
          @click="handleCollectionChange(collection)"
        />
      </template>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Bed visualizer collections (bed-visualizer-collections)
 * -----------------------------------------------------------------------------
 * Displays contents of bed visualizer collections page.
 *
 */
import { mapState } from 'vuex'
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'
import CollectionChip from '../collection-chip'
import { queryCollection } from '~/helpers/query'
import { formatProduct } from '~/helpers/format'
import product from '~/graphql/queries/product.gql'
import { getOptionValueByName, formatVariant } from '../bed-visualizer-helpers'
import { product_metafield_identifiers, variant_metafield_identifiers } from '~/helpers/metafield_identifiers'


export default {
  name: 'BedVisualizerCollections',

  components: {
    CollectionChip,
  },

  props: {
    collections: {
      type: Array,
      default: () => ([]),
    },
    categories: {
      type: Array,
      default: () => ([]),
    },

  },

  emits: ['collection-change'],

  data() {
    return {
      allProductsUnavailable: false,
      collection: {},
      collectionId: null,
      products: {},
      collectionHandle: '',
      errorMessage: false,
      queue: [],
      state: 'loading',
      tags: [],
      viewType: 'grid',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      config: (state) => state['bed-visualizer'].config,
      isMobile: (state) => state.index.screen.mobile,
    }),

    viewToggleLabel() {
      if (this.viewType === 'grid') {
        return this.$string('bedVisualizer.accessibility.gridView')
      }

      return this.$string('bedVisualizer.accessibility.listView')
    },
  },

  mounted() {
    this.updateTitle()
    disableBodyScroll(this.$refs.scrollElement)
  },

  unmounted() {
    clearAllBodyScrollLocks()
  },

  methods: {

   /**
     * Handle collection change on click.
     * @param {Object} collection - Collction object (liquid origin).
     */
    handleCollectionChange(collection) {
      this.state = 'loading'
      this.queryProducts(collection)
      this.updateTitle()
    },

    /**
     * Updates collection flow header title.
     */
    updateTitle() {
      return this.config.selectedCollection?.activeCollection?.name ?
        this.config.selectedCollection.activeCollection.name :
        this.$string('bedVisualizer.collection.title')
    },

    /**
     * Is selected collection.
     * @param {Object} collection - Collction object (liquid origin).
     * @returns {Boolean}
     */
    isSelectedCollection(collection) {
      return this.config.selectedCollection?.activeCollection?.handle === collection.handle
    },

    /**
     * Query products from GraphQL API.
     */
    async queryProducts(collection) {
      try {
        const { products, id } = await queryCollection(collection.handle)
        this.collectionId = id
        this.handles = products.map(product => product.handle)
        this.queue = this.handles.map((handle) => {
          return cnvs.Query({
            locale: cnvs.store.language,
            query: product,
            variables: {
              country: cnvs.store.country,
              handle,
              identifiers: product_metafield_identifiers,
              variant_identifiers: variant_metafield_identifiers,
            },
          })
        })

        const response = await Promise.allSettled(this.queue)

        const productsResults = response.map((promise) => {
          if (promise.status !== 'fulfilled') {
            return false
          }

          return formatProduct(promise.value)
        }).filter(Boolean)

        if (cnvs.settings.disableComponentReadyState) {
          return
        }

        /**
         * If no products returned then error.
         */
        if (!productsResults.length) {
          this.state = 'error'
          this.products = {}
          this.allProductsUnavailable = true
          return
        }

        const filteredProducts = this.filterProducts(productsResults)

        if (!filteredProducts && filteredProducts?.length < 1) { return }

        this.products =  this.formatProducts(filteredProducts)
        this.state = 'ready'

        this.$emit('collection-change', {
          selectedCollection: {
            activeCollection: {
              name: collection.title,
              handle: collection.handle,
            },
            products: this.products,
          },
        })

      } catch (error) {
        this.state = 'error'
        this.products = {}
        this.errorMessage = error
      }
    },

    /**
     * Apply filters to variants.
     * @param {Array} products - Array of product objects.
     * @returns {Array}
     */
    filterProducts(products) {
      const filterResults = products.filter((product) => {
        if (product.tags.length < 1) {
          return
        }

        const tagged = product.tags.find((tag) => {
          return tag.toLowerCase().includes('bed-visualizer')
        })

        if (!tagged) {
          return
        }

        const variants = product?.variants
        const filterNames = Object.keys(this.config.filters)
        const availableVariants = variants.filter(variant => variant.available)

        /**
         * If no filters exist return all available products.
         */
        if (!filterNames.length) {
          return availableVariants
        }

        const filteredVariants = availableVariants.filter((variant) => {
          let matchesFilters = true

          /**
           * Check all filters in config to see if variant matches.
           */
          filterNames.forEach(name => {
            const value = getOptionValueByName(variant, name)

            /**
             * If filter name doesn't exist a variant option don't proceed.
             *
             * This is to avoid hiding products when the filter can't be met
             * (e.g. a pillow can't be size:queen, so we don't want to hide it
             * if size doesn't exist as an option)
             *
             * TODO: When building out filter logic, this will have to be changed
             * so it is scoped to each unique customize step. With the exception
             * of size which should apply to headboard, bedding and sheet set.
             */

            if (!value) {
              return
            }

            if (this.config.filters[name].indexOf(value.toLowerCase()) === -1) {
              matchesFilters = false
              return
            }
          })

          return matchesFilters
        })

        return filteredVariants
      })

      return filterResults
    },

    /**
     * Filters all prouducts against the 'bed-visualizer-collection:' tag and
     * formats the available products and preselected variants.
     * @param {Array} products - Provided after filterProducts.
     * @returns {Object} Nested objects with 2 arrays each for
     *  available products and preselected variants.
     */
    formatProducts(products) {
      const mapProducts = {}
      products.forEach((product) => {
        let currentProduct = product
        let productCategory
        let allVariantBedTags = []

        /**
         * Find the category that matches the current product by tag and
         * add to 'allVariantBedTags' array for comparison later.
         */
        const category = this.categories.find((cat) => {
          if (product.tags.includes(cat.tag)) {
            productCategory = cat

            product.tags.filter((tag) => {
              if (tag.includes('bed-visualizer-collection:')) {
                const tagSplit = tag.split(':')
                const tagCollectionId = tagSplit[1]

                if (parseInt(tagCollectionId) === this.collectionId) {
                  allVariantBedTags.push(tag)
                }
              }
            })

            return product.tags.includes(cat.tag)
          }
        })

        /**
         * If a matching category exists then format and map products by category names.
         */
        if (category) {
          if (!mapProducts[category.name]) {
            mapProducts[category.name] = {
              available: [],
              preSelected: [],
            }
          }

          /**
           * If a matching category exists then mapProducts by category names.
           * If no variant specific tags exist then assume that all products are available
           * else filter variants to get ones that are tagged and map to available array.
           * If any variant has ':selected' at the end of the tag then map to preselect array.
           */
          if (allVariantBedTags.length < 1) {
            mapProducts[category.name].available.push(product)
            return mapProducts
          } else {
            const taggedVariants = product.variants.filter((variant) => {
              const currentPreselectCount = mapProducts[category.name].preSelected.length

              allVariantBedTags.find((tag) => {
                if (tag.includes(variant.id.toString()) && tag.includes(':selected')) {
                  if (productCategory.limit > currentPreselectCount) {
                    const formattedVariant = formatVariant(product, variant, productCategory.stackOrders)
                    mapProducts[category.name].preSelected.push(formattedVariant)
                  }

                  return true
                }
              })

              return variant
            })

            currentProduct.variants = taggedVariants
            mapProducts[category.name].available.push(currentProduct)
          }
        }
      })

      return mapProducts
    },

    /**
     * Toggle between grid and list view on collection selection step
     */
    toggleView() {
      this.viewType = this.viewType === 'grid' ? 'list' : 'grid'
    },
  },
}
</script>

<style lang="scss">
@import './bed-visualizer.collections';
</style>
